import React, { FC, useRef, useState } from 'react';
import { Box, Stack } from '@mui/material';
import { Delete } from '@mui/icons-material';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Slider
} from '@mui/material';
import StyledModal from '../Components/GenericModal/StyledModal';
import { sharedClasses } from '../Components/CustomUIElements/sharedClasses';
import ModalFooterButtons from '../Components/GenericModal/ModalFooterButtons';
import {
  tableSearchButtonStyles,
  weightSliderStyles,
  centerAlignStyles
} from '../shared/commonStyles';
import { classes } from '../Components/Modals/styles';
import { classes as tableClasses } from '../Components/GenericTable/styles';
import GenericDialog from '../Components/Modals/GenericDialog';

interface Condition {
  id?: number;
  content: string;
  weight: number;
  short_label?: string;
}

interface ConditionsDialogProps {
  isOpen: boolean;
  handleClose: () => void;
  conditions: Condition[];
  onConditionsChange: (conditions: Condition[]) => void;
  onSubmit: () => void;
  onResetConditions: () => void;
  submitButtonText: string;
}

const ConditionsDialog: FC<ConditionsDialogProps> = ({
  isOpen,
  handleClose,
  conditions,
  onConditionsChange,
  onSubmit,
  onResetConditions,
  submitButtonText
}) => {
  const isSubmitDisabled = conditions.length === 0;
  const mainModalContentRef = useRef<HTMLDivElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [conditionIndexToDelete, setConditionIndexToDelete] = useState<number | null>(null);
  const [refineDialogOpen, setRefineDialogOpen] = useState(false);

  const scrollToBottom = () => {
    window.requestAnimationFrame(() => {
      if (!mainModalContentRef.current) return;

      const element = mainModalContentRef.current;
      if (element.scrollHeight > element.clientHeight) {
        element.scrollTo({
          top: element.scrollHeight,
          behavior: 'smooth'
        });
      }
    });
  };

  const addCondition = () => {
    onConditionsChange([...conditions, { weight: 1, content: '' }]);
    scrollToBottom();
  };

  const handleRemoveConditionClick = (index: number) => {
    setConditionIndexToDelete(index);
    setDeleteDialogOpen(true);
  };

  const handleRemoveCondition = () => {
    if (conditionIndexToDelete !== null) {
      const newConditions = [...conditions];
      newConditions.splice(conditionIndexToDelete, 1);
      onConditionsChange(newConditions);
    }
    setDeleteDialogOpen(false);
    setConditionIndexToDelete(null);
  };

  const updateWeight = (index: number, value: number) => {
    const newConditions = [...conditions];
    newConditions[index].weight = value;
    onConditionsChange(newConditions);
  };

  const updateConditionValue = (index: number, field: keyof Condition, value: string | number) => {
    if (field === 'weight') {
      updateWeight(index, Number(value));
      return;
    }
    const newConditions = [...conditions];
    newConditions[index] = {
      ...newConditions[index],
      [field]: value
    };
    onConditionsChange(newConditions);
  };

  const handleRefineScreening = () => {
    setRefineDialogOpen(true);
  };

  const handleConfirmRefineScreening = () => {
    onSubmit();
    handleClose();
    setRefineDialogOpen(false);
  };

  const handleResetConditions = () => {
    onResetConditions();
    handleClose();
  };

  return (
    <StyledModal
      isOpen={isOpen}
      handleClose={handleClose}
      styleOverrides={{
        maxWidth: '1300px',
        maxHeight: '700px'
      }}
      label="conditions-dialog"
      modalTitle="Edit Conditions"
      subHeaderStyling={false}
    >
      <Box ref={mainModalContentRef} sx={{ ...sharedClasses.mainModalContent, height: '100%' }}>
        <Box>
          <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
            <Table
              sx={{
                ...tableClasses.tableWrapper,
                'th, td, tr, thead': {
                  padding: '8px 10px'
                }
              }}
              aria-label="conditions table"
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '80%' }}>Condition</TableCell>
                  <TableCell sx={{ width: '15%' }}>Importance</TableCell>
                  <TableCell sx={{ width: '5%' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {conditions.map((condition, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <TextField
                        value={condition.content}
                        onChange={({ target }) =>
                          updateConditionValue(index, 'content', target.value)
                        }
                        fullWidth
                        multiline
                        minRows={1}
                        maxRows={4}
                      />
                    </TableCell>
                    <TableCell>
                      <Slider
                        sx={{
                          ...classes.zoomSlider,
                          ...weightSliderStyles
                        }}
                        value={condition.weight}
                        onChange={(_, newValue) => {
                          if (typeof newValue === 'number') {
                            updateConditionValue(index, 'weight', newValue);
                          }
                        }}
                        valueLabelDisplay="auto"
                        step={1}
                        marks={[
                          { value: 1, label: 'Least' },
                          { value: 2, label: '' },
                          { value: 3, label: '' },
                          { value: 4, label: '' },
                          { value: 5, label: 'Most' }
                        ]}
                        min={1}
                        max={5}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={centerAlignStyles}>
                        <IconButton onClick={() => handleRemoveConditionClick(index)} color="error">
                          <Delete />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-between"
        mt={2}
        sx={{ width: '100%' }}
      >
        <Button
          variant="contained"
          onClick={addCondition}
          sx={{ ...tableSearchButtonStyles, whiteSpace: 'nowrap' }}
        >
          Add Condition
        </Button>
        <ModalFooterButtons
          primaryButtonText={submitButtonText}
          primaryButtonCallback={handleRefineScreening}
          primaryButtonDisabled={isSubmitDisabled}
          secondaryButtonText="Cancel"
          secondaryButtonCallback={handleResetConditions}
          primaryButtonID="submit-button"
          secondaryButtonID="cancel-conditions"
          stylesOverride={{ justifyContent: 'flex-end' }}
        />
      </Stack>

      <GenericDialog
        isDialogOpen={deleteDialogOpen}
        setDialogOpen={setDeleteDialogOpen}
        title="Remove condition?"
        description="Are you sure you want to remove condition? This action cannot be undone."
        buttonCallback={handleRemoveCondition}
        callbackLoading={false}
        buttonText="Remove"
        url=""
      />

      <GenericDialog
        isDialogOpen={refineDialogOpen}
        setDialogOpen={setRefineDialogOpen}
        title="Regenerate results"
        description="We noticed you updated your conditions. Once confirmed, candidate results will be regenerated. Do you want to continue?"
        buttonCallback={handleConfirmRefineScreening}
        callbackLoading={false}
        buttonText="Continue"
        url=""
        alternateColor={true}
      />
    </StyledModal>
  );
};

export default ConditionsDialog;
